import React, { FC } from 'react';
import { MessageCard, PageHeader, UserStateComponent } from '../../components';
import { ChildRegistrations } from '../../components/ChildRegistrations';
import { ChildRegistration } from '../../models';
import { useTranslation } from '../../translations';
import { Fetcher, isLogicError, isResourceError, useFetcher, useUserSafe } from '../../utils';
import { LogicError } from '../../utils/errorHandling';

type LoadingData = [ChildRegistration[], number];

const NotRegistered: FC = () => {
  const { ts } = useTranslation();
  return (
    <MessageCard icon="error_outline" level="danger" title={ts('you_have_not_registered_yet')}>
      {ts('in_order_to_register_children')}
    </MessageCard>
  );
};

const ChildrenNotAllowed: FC = () => {
  const { ts } = useTranslation();
  return (
    <MessageCard icon="remove_circle" level="danger" title={ts('child_registrations_not_allowed')}>
      {ts('the_event_policy_currently_disallows')}
    </MessageCard>
  );
};

const ParentCannotVolunteer: FC = () => {
  const { ts } = useTranslation();
  return (
    <MessageCard icon="face" id="parentCanNotVolunteer" level="warning">
      {ts('common.no_human_being_can_possibly')}
    </MessageCard>
  );
};

const fetchData = async (userId: number): Promise<LoadingData> => {
  return await Promise.all([
    api.getCurrentUserChildren(),
    api.getUserActiveRegistration(userId).then((d) => d.id),
  ]);
};

export const ChildRegistrationComponent: FC = () => {
  const { ts } = useTranslation();
  const userId = useUserSafe().id;
  const fetcher = useFetcher(async () => await fetchData(userId), [userId]);

  const { error } = fetcher;
  if (error) {
    if (isResourceError(error, 'Registration')) {
      return <NotRegistered />;
    }

    if (isLogicError(error, LogicError.ChildrenNotAllowed)) {
      return <ChildrenNotAllowed />;
    }

    if (isLogicError(error, LogicError.ParentCannotVolunteer)) {
      return <ParentCannotVolunteer />;
    }
  }

  if (!fetcher.data) {
    return <Fetcher inline result={fetcher} />;
  }

  const [children, registrationId] = fetcher.data;

  return (
    <UserStateComponent>
      <PageHeader>{ts('register_children')}</PageHeader>
      <ChildRegistrations
        items={children}
        onUpdate={fetcher.refresh}
        registrationId={registrationId}
      />
    </UserStateComponent>
  );
};
